import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import confetti from "canvas-confetti";
import Layout from "../components/Layout";
import MapView from "../components/MapView";
import FuzzyAreaMap from "../components/FuzzyAreaMap";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { api, API, getUser, stripeReturnOrigin, isAdminUser } from "../lib/api";
import { LOAD_SIZE_LABEL } from "./PostWaste";
import { timeAgo } from "./Marketplace";
import { openExternal } from "../lib/native";
import { toast } from "sonner";
import { Clock, MapPin, ShieldCheck, ArrowLeft, Calendar, Trash, Star, Gavel, Lock, Unlock, Edit3, Flag, Sparkles, Home as HomeIcon, Truck as TruckIcon } from "lucide-react";
import { StarRating } from "../components/StarRating";
import ChatPanel from "../components/ChatPanel";
import ReportDialog from "../components/ReportDialog";
import BlockUserButton from "../components/BlockUserButton";
import { capture } from "../lib/analytics";

// Reveal-on-Win helpers.
// The server masks addresses down to "Town + outward postcode" (e.g.
// "London W1G") for anyone who isn't the owner, an admin, or the winning
// carrier. A masked value therefore contains no digits or comma-separated
// street segments — we use that shape to detect masking client-side so
// we can flip the UI from "hidden" → "revealed" the moment the bid is
// accepted (post-payment). See deps.public_area_label() for the mask.
function isAddressMasked(addr) {
  if (!addr) return true;
  // A full UK address always contains at least one digit (house number or
  // inward postcode). Masked area labels ("London W1G", "Manchester M1")
  // may contain a digit inside the outward postcode district — so instead
  // check for a comma OR a house-number pattern at the start.
  if (addr.includes(",")) return false;
  if (/^\d+\s/.test(addr.trim())) return false;
  // A full inward postcode is present (e.g. "SW1A 2AA") when there's a
  // trailing "<digit><letter><letter>" after a space.
  if (/\b\d[A-Z]{2}\b/i.test(addr)) return false;
  return true;
}

function fireRevealConfetti() {
  // Two-burst 2-second subtle celebration in Binshare brand colours.
  const colors = ["#1B3624", "#4A6B56", "#D67B54", "#EBE8E0"];
  const defaults = { spread: 70, ticks: 90, gravity: 0.9, decay: 0.94, scalar: 0.9, colors };
  confetti({ ...defaults, particleCount: 60, angle: 60, origin: { x: 0.15, y: 0.7 } });
  confetti({ ...defaults, particleCount: 60, angle: 120, origin: { x: 0.85, y: 0.7 } });
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 40, angle: 90, origin: { x: 0.5, y: 0.6 }, startVelocity: 35 });
  }, 350);
}

export default function WasteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = getUser();
  const [post, setPost] = useState(null);
  const [bids, setBids] = useState([]);
  const [bid, setBid] = useState({ amount: "", message: "", pickup_at: "", eta_hours: 24 });
  const [paying, setPaying] = useState(null);
  const [reviews, setReviews] = useState({});
  const [customerRating, setCustomerRating] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [customerReviewForm, setCustomerReviewForm] = useState({ rating: 5, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [submittingCustReview, setSubmittingCustReview] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [escrow, setEscrow] = useState(null);
  const [filingDispute, setFilingDispute] = useState(false);
  const [amending, setAmending] = useState(false);
  const [respondingAmend, setRespondingAmend] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  // F-05: per-bid carrier reporting — {carrierId, carrierName} while open,
  // null when the dialog is closed.
  const [reportCarrier, setReportCarrier] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [disposalSite, setDisposalSite] = useState("");
  // Phase 5.1 — reuse breakdown captured at completion.
  const [reusedPct, setReusedPct] = useState("");
  const [recycledPct, setRecycledPct] = useState("");
  const [landfilledPct, setLandfilledPct] = useState("");
  const [disposalNotes, setDisposalNotes] = useState("");
  // Task 1 — Price variation dialog (carrier proposes revised price on-site).
  const [variationOpen, setVariationOpen] = useState(false);
  const [variationForm, setVariationForm] = useState({ new_amount: "", reason: "", photo_url: "" });
  const [variationUploading, setVariationUploading] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/waste-posts/${id}`, { params: user?.id ? { viewer_id: user.id } : {} });
    setPost(data);
    // Bid visibility is enforced server-side:
    //   - post owner → all bids
    //   - carrier    → only their own bid on this post
    //   - anyone else → 403 (caught below, bids stay [])
    let b = [];
    try {
      const res = await api.get(`/waste-posts/${id}/bids`, { params: { user_id: user?.id } });
      b = res.data;
    } catch {
      b = [];
    }
    setBids(b);
    // Fetch carrier ratings + customer rating
    const carriers = [...new Set(b.map((x) => x.carrier_id))];
    const r = {};
    await Promise.all(
      carriers.map(async (cid) => {
        try {
          const res = await api.get(`/carriers/${cid}/reviews`);
          r[cid] = res.data;
        } catch (e) {
          console.warn(`[waste-detail] failed to load reviews for carrier ${cid}`, e);
        }
      })
    );
    setReviews(r);
    try {
      const cr = await api.get(`/customers/${data.user_id}/reviews`);
      setCustomerRating(cr.data);
    } catch (e) {
      console.warn(`[waste-detail] failed to load customer rating for ${data.user_id}`, e);
    }
    // If completed, look up escrow status for this job from carrier side
    if (data?.status === "completed" && data?.accepted_bid_id && user) {
      const acceptedBid = b.find((x) => x.id === data.accepted_bid_id);
      if (acceptedBid) {
        try {
          const er = await api.get(`/escrow/carrier/${acceptedBid.carrier_id}`);
          const match = er.data.items.find((x) => x.post_id === id);
          setEscrow(match || null);
        } catch (e) {
          console.warn(`[waste-detail] failed to load escrow for bid ${acceptedBid.id}`, e);
        }
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    // When the app returns to the foreground after a Stripe checkout on
    // native iOS/Android, refetch the post so the reviewer sees the
    // updated status (accepted → paid) without needing to pull-to-refresh.
    // Stripe redirects to the production website in Safari, then the
    // reviewer manually returns to the app — this listener catches that
    // return and syncs the bid state. Also runs when the tab is refocused
    // on desktop web for the same reason.
    let capListener;
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (Capacitor.isNativePlatform()) {
          const { App } = await import("@capacitor/app");
          capListener = await App.addListener("appStateChange", (state) => {
            if (state.isActive) load();
          });
        }
      } catch (e) {
        // Capacitor not available (web) — fall through to the visibility listener below.
      }
    })();
    const onVisibility = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      try { capListener && capListener.remove(); } catch { /* noop */ }
    };
    // eslint-disable-next-line
  }, [id]);

  // Reveal-on-Win — fire a subtle confetti burst the moment the winning
  // carrier's bid is accepted (which only happens post-payment upstream).
  // MUST live before the `if (!post) return …` early-return below so React's
  // hook order is stable across renders.
  const _acceptedBidForReveal = post && bids.find((b) => b.id === post.accepted_bid_id);
  const _isAcceptedCarrierForReveal = !!(_acceptedBidForReveal && user?.id === _acceptedBidForReveal.carrier_id);
  const _addressRevealedForReveal =
    _isAcceptedCarrierForReveal && post?.address && !isAddressMasked(post.address);
  useEffect(() => {
    if (!_addressRevealedForReveal || !post?.id || !user?.id) return;
    const key = `binshare.address-reveal-celebrated:${post.id}:${user.id}`;
    if (typeof window === "undefined" || window.localStorage.getItem(key)) return;
    window.localStorage.setItem(key, "1");
    try { fireRevealConfetti(); } catch { /* noop */ }
  }, [_addressRevealedForReveal, post?.id, user?.id]);

  if (!post) return <Layout><div className="max-w-7xl mx-auto px-5 py-12">Loading…</div></Layout>;

  const isOwner = user?.id === post.user_id;
  const canBid = user?.role === "carrier" && post.status === "open";

  const submitBid = async (e) => {
    e.preventDefault();
    try {
      await api.post(
        `/waste-posts/${id}/bids`,
        { amount: Number(bid.amount), message: bid.message, eta_hours: Number(bid.eta_hours), pickup_at: bid.pickup_at },
        { params: { user_id: user.id } }
      );
      capture("bid_submitted", {
        amount: Number(bid.amount),
        eta_hours: Number(bid.eta_hours),
        waste_post_id: id,
      });
      toast.success("Bid submitted!");
      setBid({ amount: "", message: "", pickup_at: "", eta_hours: 24 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const acceptBid = async (b) => {
    setPaying(b.id);
    try {
      // On native Capacitor the current origin is `capacitor://localhost`,
      // which Stripe can't redirect back to. `stripeReturnOrigin()` returns
      // the production HTTPS host on native (so Stripe redirects Safari to
      // https://app.binshare.co.uk/payment-success) and falls through to
      // window.location.origin on the web.
      const origin = stripeReturnOrigin();
      const { data } = await api.post(`/payments/checkout/session`, {
        kind: "bid",
        target_id: b.id,
        origin_url: origin,
        user_id: user.id,
      });
      // F-01: Route Stripe checkout through @capacitor/browser
      // (SFSafariViewController on iOS) so we never kick the user out to
      // the external Safari app — Guideline 4.
      await openExternal(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment init failed");
      setPaying(null);
    }
  };

  const acceptedBid = bids.find((b) => b.id === post?.accepted_bid_id);
  const isAcceptedCarrier = acceptedBid && user?.id === acceptedBid.carrier_id;
  const canMarkComplete = post?.status === "accepted" && (isOwner || isAcceptedCarrier);
  const canReview = isOwner && post?.status === "completed" && acceptedBid;
  const canReviewCustomer = isAcceptedCarrier && post?.status === "completed";
  // Reveal-on-Win: the backend unmasks the address for the winning carrier
  // the moment their bid is accepted (which only happens after Stripe
  // confirms payment). See the confetti effect declared BEFORE the early
  // return above — this local re-computes the same boolean for the JSX.
  const addressRevealed = isAcceptedCarrier && post?.address && !isAddressMasked(post.address);
  const canDispute = post?.status === "completed" && (isOwner || isAcceptedCarrier)
    && escrow && (escrow.escrow_status === "held" || escrow.escrow_status === "released" || escrow.escrow_status === undefined);
  // Once disputed or refunded, can't dispute again
  const alreadyDisputed = escrow && ["disputed", "refunded"].includes(escrow.escrow_status);

  const markComplete = async () => {
    // Carriers MUST supply the disposal site / method AND a reuse
    // breakdown (Phase 5 duty-of-care + Phase 5.1 reuse tracking).
    // Homeowners can still trigger completion without either — kept as a
    // fallback path so a customer isn't stuck if the carrier forgot.
    if (isAcceptedCarrier) {
      if (!disposalSite.trim() || reusedPct === "" || recycledPct === "" || landfilledPct === "") {
        setCompleteOpen(true);
        return;
      }
    }
    const r = Number.parseInt(reusedPct || "0", 10);
    const c = Number.parseInt(recycledPct || "0", 10);
    const l = Number.parseInt(landfilledPct || "0", 10);
    if (isAcceptedCarrier && r + c + l !== 100) {
      toast.error("Reused + Recycled + Landfilled must add up to 100%");
      return;
    }
    setCompleting(true);
    try {
      await api.post(
        `/waste-posts/${id}/complete`,
        {
          disposal_site: disposalSite.trim() || null,
          reused_pct: isAcceptedCarrier ? r : null,
          recycled_pct: isAcceptedCarrier ? c : null,
          landfilled_pct: isAcceptedCarrier ? l : null,
          disposal_notes: disposalNotes.trim() || null,
        },
        { params: { user_id: user.id } }
      );
      toast.success("Job marked complete — escrow window started");
      setCompleteOpen(false);
      setDisposalSite("");
      setReusedPct(""); setRecycledPct(""); setLandfilledPct(""); setDisposalNotes("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setCompleting(false);
    }
  };

  const requestAmendment = () => {
    setVariationForm({ new_amount: acceptedBid ? String(acceptedBid.amount.toFixed(2)) : "", reason: "", photo_url: "" });
    setVariationOpen(true);
  };

  const uploadVariationPhoto = async (file) => {
    if (!file) return;
    setVariationUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/upload?user_id=${user.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${API}/files/${data.path}`;
      setVariationForm((f) => ({ ...f, photo_url: url }));
      toast.success("Photo attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setVariationUploading(false);
    }
  };

  const submitVariation = async () => {
    const parsed = Number(variationForm.new_amount);
    if (!parsed || parsed <= 0) {
      toast.error("Enter a valid new price");
      return;
    }
    if (!variationForm.reason.trim()) {
      toast.error("Please add a reason");
      return;
    }
    if (Math.abs(parsed - Number(acceptedBid.amount)) < 0.01) {
      toast.error("New price must differ from the current quote");
      return;
    }
    setAmending(true);
    try {
      await api.post(
        `/bids/${acceptedBid.id}/amend`,
        { new_amount: parsed, reason: variationForm.reason.trim(), photo_url: variationForm.photo_url || null },
        { params: { user_id: user.id } }
      );
      toast.success("Variation request sent to customer for approval");
      setVariationOpen(false);
      setVariationForm({ new_amount: "", reason: "", photo_url: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setAmending(false);
    }
  };

  const respondAmendment = async (action) => {
    setRespondingAmend(true);
    try {
      const { data } = await api.post(
        `/bids/${acceptedBid.id}/amend/respond`,
        { action },
        { params: { user_id: user.id } }
      );
      if (action === "accept" && data.topup_url) {
        toast.success("Redirecting to pay top-up…");
        // F-01: in-app Safari View so we never leave Binshare.
        await openExternal(data.topup_url);
        return;
      }
      toast.success(action === "accept" ? "Amendment accepted" : "Amendment rejected — original quote stands");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setRespondingAmend(false);
    }
  };

  const fileDispute = async () => {
    const reason = window.prompt("Describe the issue (this will go to Binshare admin):");
    if (!reason || !reason.trim()) return;
    setFilingDispute(true);
    try {
      await api.post(
        `/waste-posts/${id}/dispute`,
        { reason: reason.trim() },
        { params: { user_id: user.id } }
      );
      toast.success("Dispute filed — escrow frozen, admin notified");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setFilingDispute(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!acceptedBid) return;
    setSubmittingReview(true);
    try {
      await api.post(
        `/carriers/${acceptedBid.carrier_id}/reviews`,
        { rating: reviewForm.rating, comment: reviewForm.comment },
        { params: { user_id: user.id, waste_post_id: post.id } }
      );
      toast.success("Thanks for the review!");
      setReviewForm({ rating: 5, comment: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmittingReview(false);
    }
  };

  const submitCustomerReview = async (e) => {
    e.preventDefault();
    setSubmittingCustReview(true);
    try {
      await api.post(
        `/customers/${post.user_id}/reviews`,
        { rating: customerReviewForm.rating, comment: customerReviewForm.comment },
        { params: { user_id: user.id, waste_post_id: post.id } }
      );
      toast.success("Thanks for rating the customer!");
      setCustomerReviewForm({ rating: 5, comment: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmittingCustReview(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-[#6B6965] hover:text-[#1B3624] min-h-[40px]">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mt-4 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="overflow-hidden rounded-2xl border-[#E2DFD8] p-0">
              {(() => {
                // Prefer the new `image_urls` array; fall back to legacy
                // single `image_url` so old posts still render a hero.
                const gallery = (post.image_urls && post.image_urls.length > 0)
                  ? post.image_urls
                  : (post.image_url ? [post.image_url] : []);
                if (gallery.length === 0) return null;
                if (gallery.length === 1) {
                  return <img src={gallery[0]} alt="" className="w-full h-64 object-cover" />;
                }
                return (
                  <div data-testid="waste-gallery" className="flex overflow-x-auto snap-x snap-mandatory scrollbar-thin">
                    {gallery.map((u, i) => (
                      <img
                        key={`${u}-${i}`}
                        data-testid={`waste-gallery-photo-${i}`}
                        src={u}
                        alt={`Waste photo ${i + 1} of ${gallery.length}`}
                        className="w-full flex-shrink-0 h-64 object-cover snap-center"
                      />
                    ))}
                  </div>
                );
              })()}
              <div className="p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px]">{post.waste_type}</Badge>
                  {post.job_type === "clear" && post.job_subtype && (
                    <Badge
                      data-testid="waste-subtype-badge"
                      className="bg-[#1B3624] text-white hover:bg-[#1B3624] uppercase text-[10px]"
                    >
                      Clear · {SUBTYPE_LABELS[post.job_subtype] || post.job_subtype}
                    </Badge>
                  )}
                  <Badge className={`uppercase text-[10px] ${post.status === "open" ? "bg-[#D67B54] text-white" : post.status === "completed" ? "bg-[#1B3624] text-white" : "bg-[#4A6B56] text-white"}`}>
                    {post.status}
                  </Badge>
                  {post.load_size && LOAD_SIZE_LABEL[post.load_size] && (
                    <Badge
                      data-testid="waste-load-size-badge"
                      className="uppercase text-[10px] tracking-wider bg-[#4A6B56]/10 text-[#1B3624] hover:bg-[#4A6B56]/10 border border-[#4A6B56]/25"
                    >
                      {LOAD_SIZE_LABEL[post.load_size]}
                    </Badge>
                  )}
                  {typeof post.is_residential === "boolean" && (
                    <Badge
                      data-testid="waste-residential-badge"
                      className={`uppercase text-[10px] tracking-wider inline-flex items-center gap-1 ${
                        post.is_residential
                          ? "bg-[#4A6B56]/10 text-[#1B3624] hover:bg-[#4A6B56]/10 border border-[#4A6B56]/25"
                          : "bg-[#D67B54]/10 text-[#B84E2A] hover:bg-[#D67B54]/10 border border-[#D67B54]/40"
                      }`}
                    >
                      <HomeIcon className="w-3 h-3" />
                      {post.is_residential ? "Residential" : "Commercial"}
                    </Badge>
                  )}
                  {typeof post.easy_access === "boolean" && (
                    <Badge
                      data-testid="waste-access-badge"
                      className={`uppercase text-[10px] tracking-wider inline-flex items-center gap-1 ${
                        post.easy_access
                          ? "bg-[#4A6B56]/10 text-[#1B3624] hover:bg-[#4A6B56]/10 border border-[#4A6B56]/25"
                          : "bg-[#D67B54]/10 text-[#B84E2A] hover:bg-[#D67B54]/10 border border-[#D67B54]/40"
                      }`}
                    >
                      <TruckIcon className="w-3 h-3" />
                      {post.easy_access ? "Easy access" : "Restricted access"}
                    </Badge>
                  )}
                </div>
                <h1 data-testid="waste-detail-title" className="mt-3 text-3xl sm:text-4xl tracking-tighter font-medium" style={{ fontFamily: "Outfit" }}>
                  {post.title}
                </h1>
                <div className="mt-3 text-sm text-[#6B6965] flex flex-wrap gap-x-6 gap-y-1">
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {post.address}</span>
                  <span className="flex items-center gap-1"><Trash className="w-4 h-4" /> {post.volume}</span>
                  {post.preferred_date && <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {post.preferred_date}</span>}
                  {post.created_at && (
                    <span data-testid="waste-posted-ago" className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> Posted {timeAgo(post.created_at)}
                    </span>
                  )}
                </div>
                <p className="mt-4 text-[#1A1A1A] leading-relaxed">{post.description}</p>
                {post.job_type === "clear" && post.job_subtype && post.clearance_details && Object.keys(post.clearance_details).length > 0 && (
                  <ClearanceDetails subtype={post.job_subtype} details={post.clearance_details} />
                )}
                {post.access_details && (
                  <div data-testid="waste-access-details" className="mt-5 rounded-2xl border border-[#D67B54]/30 bg-[#D67B54]/5 p-4">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#D67B54]" />
                      <div className="text-[11px] uppercase tracking-widest text-[#D67B54] font-medium">Collection &amp; Access</div>
                    </div>
                    <p className="mt-2 text-sm text-[#1B3624] leading-relaxed whitespace-pre-wrap">{post.access_details}</p>
                  </div>
                )}
                <div className="mt-4 text-sm text-[#6B6965] flex flex-wrap items-center gap-2">
                  Posted by <span className="text-[#1B3624] font-medium">{post.user_name}</span>
                  {customerRating && customerRating.count > 0 && (
                    <span data-testid="customer-rating" className="inline-flex items-center gap-1 text-xs">
                      <StarRating value={customerRating.average} size={12} readOnly testId="customer-stars" />
                      {customerRating.average} · {customerRating.count} review{customerRating.count > 1 ? "s" : ""}
                    </span>
                  )}
                  {user && post.user_id && user.id !== post.user_id && (
                    <div className="ml-auto flex items-center gap-3">
                      <button
                        type="button"
                        data-testid="report-waste-post"
                        onClick={() => setReportOpen(true)}
                        className="inline-flex items-center gap-1 text-xs text-[#6B6965] hover:text-[#D67B54] underline underline-offset-2"
                      >
                        <Flag className="w-3 h-3" /> Report post
                      </button>
                      <BlockUserButton
                        currentUserId={user.id}
                        targetUserId={post.user_id}
                        targetUserName={post.user_name}
                        label="Block poster"
                        testId="block-waste-poster"
                      />
                    </div>
                  )}
                </div>
                {post.ref_code && (
                  <div
                    data-testid="waste-ref-code"
                    className="mt-4 flex justify-end text-[11px] text-[#9B9893] font-mono uppercase tracking-widest"
                  >
                    Ref&nbsp;{post.ref_code}
                  </div>
                )}
              </div>
            </Card>

            {/* Reveal-on-Win — full pickup address unlocks for the winning
                carrier the moment their bid is accepted (payment already
                cleared upstream). Confetti fires once (see fireRevealConfetti
                effect above). Fuzzy-area map hides for this carrier since
                they now have the exact address. */}
            {addressRevealed && (
              <Card
                data-testid="address-revealed-card"
                className="rounded-2xl border-[#4A6B56] p-6 bg-gradient-to-br from-[#4A6B56]/8 via-white to-[#D67B54]/10 relative overflow-hidden"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-[#4A6B56] text-white flex items-center justify-center">
                      <Unlock className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#4A6B56] font-semibold">
                        <Sparkles className="w-3 h-3" /> You won this job
                      </div>
                      <h2
                        className="text-xl tracking-tight font-medium mt-0.5"
                        style={{ fontFamily: "Outfit" }}
                      >
                        Address unlocked
                      </h2>
                    </div>
                  </div>
                  <Button
                    data-testid="copy-revealed-address"
                    variant="outline"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(post.address);
                        toast.success("Address copied");
                      } catch {
                        toast.error("Couldn't copy — long-press to select");
                      }
                    }}
                    className="rounded-full border-[#1B3624] text-[#1B3624] hover:bg-[#1B3624]/5 min-h-[40px] px-4"
                  >
                    Copy
                  </Button>
                </div>
                <div
                  data-testid="revealed-address"
                  className="mt-4 text-lg sm:text-xl text-[#1B3624] font-medium leading-snug flex items-start gap-2"
                  style={{ fontFamily: "Outfit" }}
                >
                  <MapPin className="w-5 h-5 text-[#D67B54] mt-1 shrink-0" />
                  <span>{post.address}</span>
                </div>
                <p className="mt-3 text-xs text-[#6B6965] leading-relaxed">
                  The customer has paid — this is the full pickup address. Keep it private and use it only for this collection.
                </p>
              </Card>
            )}

            {/* Task 9 — carrier-only fuzzy area map. Sits between the job
                info and the bid form so a carrier can eyeball the area
                before scrolling to place a bid. Customers, signed-out
                visitors and admins never render this card (admin still
                has their precise MapView in the right column). Once the
                carrier wins the bid the exact address is revealed above,
                so the fuzzy circle no longer serves them. */}
            {user?.role === "carrier" && !isAdminUser(user) && !isAcceptedCarrier && (
              <Card data-testid="carrier-fuzzy-map-card" className="rounded-2xl border-[#E2DFD8] p-6 bg-white">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h2 className="text-xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>
                    Collection area
                  </h2>
                  <span className="text-[10px] uppercase tracking-widest text-[#6B6965]">
                    Carrier-only
                  </span>
                </div>
                <div className="mt-3">
                  <FuzzyAreaMap
                    lat={post.lat}
                    lng={post.lng}
                    postId={post.id}
                    testId="carrier-fuzzy-map"
                  />
                </div>
                <p className="mt-3 text-[12px] text-[#6B6965] leading-snug">
                  Approximate 500 m area based on the job's postcode district. The exact address is shared once you win the bid.
                </p>
              </Card>
            )}

            <Card className="rounded-2xl border-[#E2DFD8] p-6 bg-[#F6F4F0]">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <h2 className="text-xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>
                  {isOwner ? `Bids (${bids.length})` : "Your bid"}
                </h2>
                {!isOwner && user?.role === "carrier" && (
                  <span data-testid="bid-privacy-note" className="text-[11px] text-[#6B6965] italic">
                    Only your bid is visible — competitor amounts are private.
                  </span>
                )}
              </div>
              {bids.length === 0 && <div className="mt-3 text-sm text-[#6B6965]">No bids yet.</div>}
              <div className="mt-4 space-y-3">
                {bids.map((b) => (
                  <Card key={b.id} data-testid={`bid-row-${b.id}`} className="p-4 rounded-2xl border-[#E2DFD8] bg-white">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium" style={{ fontFamily: "Outfit" }}>{b.carrier_name}</div>
                          {reviews[b.carrier_id] && reviews[b.carrier_id].count > 0 && (
                            <div data-testid={`carrier-rating-${b.carrier_id}`} className="flex items-center gap-1 text-xs text-[#6B6965]">
                              <StarRating value={reviews[b.carrier_id].average} size={12} readOnly testId={`stars-${b.carrier_id}`} />
                              <span>{reviews[b.carrier_id].average} · {reviews[b.carrier_id].count} reviews</span>
                            </div>
                          )}
                          {b.carrier_license && (
                            <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] text-[10px] uppercase">
                              <ShieldCheck className="w-3 h-3 mr-1" /> {b.carrier_license}
                            </Badge>
                          )}
                          {b.status === "accepted" && <Badge className="bg-[#4A6B56] text-white hover:bg-[#4A6B56] text-[10px] uppercase">Accepted</Badge>}
                          {b.status === "rejected" && <Badge className="bg-[#EBE8E0] text-[#6B6965] hover:bg-[#EBE8E0] text-[10px] uppercase">Rejected</Badge>}
                        </div>
                        <div className="text-sm text-[#6B6965] mt-1">{b.message}</div>
                        <div className="text-xs text-[#6B6965] mt-1 flex items-center gap-3">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {b.pickup_at ? `Pickup ${new Date(b.pickup_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}` : `ETA ${b.eta_hours}h`}</span>
                        </div>
                        {user && b.carrier_id && user.id !== b.carrier_id && (
                          <div className="mt-2 flex items-center gap-3">
                            <button
                              type="button"
                              data-testid={`report-carrier-${b.carrier_id}`}
                              onClick={() => setReportCarrier({ id: b.carrier_id, name: b.carrier_name })}
                              className="inline-flex items-center gap-1 text-xs text-[#6B6965] hover:text-[#D67B54] underline underline-offset-2"
                            >
                              <Flag className="w-3 h-3" /> Report
                            </button>
                            <BlockUserButton
                              currentUserId={user.id}
                              targetUserId={b.carrier_id}
                              targetUserName={b.carrier_name}
                              label="Block"
                              testId={`block-carrier-${b.carrier_id}`}
                            />
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-medium" style={{ fontFamily: "Outfit" }}>£{b.amount.toFixed(2)}</div>
                        <div className="text-[10px] text-[#6B6965] mt-0.5">
                          Carrier receives £{(b.amount * 0.95).toFixed(2)} (Binshare keeps 5%)
                        </div>
                        {isOwner && post.status === "open" && (
                          <Button
                            data-testid={`accept-bid-${b.id}`}
                            disabled={paying === b.id}
                            onClick={() => acceptBid(b)}
                            className="mt-2 rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white min-h-[44px]"
                          >
                            {paying === b.id ? "Redirecting…" : `Accept · £${b.amount.toFixed(2)}`}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>

            {canMarkComplete && (
              <Card data-testid="complete-card" className="rounded-2xl border-[#E2DFD8] p-6 bg-[#F6F4F0] flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>Job in progress</h2>
                  <p className="text-sm text-[#6B6965] mt-1">
                    {isOwner ? "Once the carrier finishes the pickup, mark the job complete. Funds release to the carrier 24h after — unless you file a dispute." : "When you've completed the pickup, mark the job complete. Your payout will be held in escrow for 24h."}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {isAcceptedCarrier && acceptedBid?.last_amendment_status !== "pending" && (
                    <Button
                      data-testid="request-variation-btn"
                      onClick={requestAmendment}
                      disabled={amending}
                      variant="outline"
                      className="rounded-full border-[#D67B54] text-[#D67B54] hover:bg-[#FDF2EB] min-h-[44px]"
                    >
                      <Edit3 className="w-4 h-4 mr-1" /> {amending ? "Sending…" : "Request Price Variation"}
                    </Button>
                  )}
                  <Button
                    data-testid="mark-complete-btn"
                    onClick={markComplete}
                    disabled={completing || acceptedBid?.last_amendment_status === "pending"}
                    className="rounded-full bg-[#4A6B56] hover:bg-[#3d5947] text-white px-6 min-h-[44px]"
                  >
                    {completing ? "Marking…" : "Mark as completed"}
                  </Button>
                </div>
              </Card>
            )}

            {/* Pending price variation notice — visible to both parties */}
            {post.status === "accepted" && acceptedBid?.last_amendment_status === "pending" && (
              <Card data-testid="variation-pending-card" className="rounded-2xl border-[#D67B54] bg-[#FDF2EB] p-6">
                <h2 className="text-lg tracking-tight font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                  <Edit3 className="w-5 h-5 text-[#D67B54]" /> Price variation requested
                </h2>
                <div className="mt-3 text-sm">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <span className="text-[#6B6965]">Original</span>
                    <span className="line-through text-[#6B6965]">£{acceptedBid.amount.toFixed(2)}</span>
                    <span className="text-[#6B6965]">→ Proposed</span>
                    <span className="text-2xl font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>
                      £{Number(acceptedBid.pending_amendment_amount || 0).toFixed(2)}
                    </span>
                  </div>
                  {acceptedBid.pending_amendment_reason && (
                    <p className="mt-2 text-[#1A1A1A]">
                      <span className="text-[#6B6965]">Reason: </span>{acceptedBid.pending_amendment_reason}
                    </p>
                  )}
                  {acceptedBid.pending_amendment_photo_url && (
                    <a
                      href={acceptedBid.pending_amendment_photo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => { e.preventDefault(); openExternal(acceptedBid.pending_amendment_photo_url); }}
                      data-testid="variation-pending-photo"
                      className="mt-3 inline-block rounded-xl overflow-hidden border border-[#E2DFD8]"
                    >
                      <img
                        src={acceptedBid.pending_amendment_photo_url}
                        alt="On-site evidence"
                        className="w-full max-w-xs h-auto"
                      />
                    </a>
                  )}
                  {isOwner ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        data-testid="variation-accept-btn"
                        onClick={() => respondAmendment("accept")}
                        disabled={respondingAmend}
                        className="rounded-full bg-[#4A6B56] hover:bg-[#3d5947] text-white min-h-[44px]"
                      >
                        Accept new price
                      </Button>
                      <Button
                        data-testid="variation-decline-btn"
                        onClick={() => respondAmendment("reject")}
                        disabled={respondingAmend}
                        variant="outline"
                        className="rounded-full border-[#6B6965] text-[#6B6965] min-h-[44px]"
                      >
                        Decline — keep original
                      </Button>
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-[#6B6965]">Waiting on the customer to accept or decline.</p>
                  )}
                </div>
              </Card>
            )}

            {/* Variation history — resolved requests preserved on the job record */}
            {(isOwner || isAcceptedCarrier) && Array.isArray(acceptedBid?.amendments) && acceptedBid.amendments.filter((a) => a.status !== "pending").length > 0 && (
              <Card data-testid="variation-history-card" className="rounded-2xl border-[#E2DFD8] p-6 bg-white">
                <h2 className="text-lg tracking-tight font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                  <Edit3 className="w-5 h-5 text-[#6B6965]" /> Price variation history
                </h2>
                <ul className="mt-3 space-y-3 text-sm">
                  {acceptedBid.amendments.filter((a) => a.status !== "pending").map((a) => (
                    <li key={a.id} data-testid={`variation-history-${a.id}`} className="border border-[#F0EEE8] rounded-xl p-3">
                      <div className="flex flex-wrap items-baseline gap-2 justify-between">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="text-[#6B6965]">£{Number(a.original_price).toFixed(2)}</span>
                          <span className="text-[#6B6965]">→</span>
                          <span className="font-medium text-[#1B3624]">£{Number(a.proposed_price).toFixed(2)}</span>
                        </div>
                        <Badge className={`uppercase text-[10px] ${a.status === "accepted" ? "bg-[#4A6B56] text-white hover:bg-[#4A6B56]" : "bg-[#EBE8E0] text-[#6B6965] hover:bg-[#EBE8E0]"}`}>
                          {a.status === "accepted" ? "Accepted" : "Declined"}
                        </Badge>
                      </div>
                      {a.reason && <p className="mt-2 text-[#1A1A1A]"><span className="text-[#6B6965]">Reason: </span>{a.reason}</p>}
                      {a.photo_url && (
                        <a
                          href={a.photo_url}
                          onClick={(e) => { e.preventDefault(); openExternal(a.photo_url); }}
                          className="mt-2 inline-block rounded-lg overflow-hidden border border-[#E2DFD8]"
                        >
                          <img src={a.photo_url} alt="Variation evidence" className="max-w-[220px] h-auto" />
                        </a>
                      )}
                      {a.resolved_at && (
                        <p className="mt-2 text-[11px] text-[#6B6965]">
                          {a.status === "declined" ? "Declined by customer" : "Accepted by customer"} · {new Date(a.resolved_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Escrow & dispute panel — visible to both parties after completion */}
            {post.status === "completed" && (isOwner || isAcceptedCarrier) && escrow && (
              <Card data-testid="escrow-panel" className="rounded-2xl border-[#E2DFD8] p-6 bg-white">
                <h2 className="text-lg tracking-tight font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                  <Lock className="w-5 h-5 text-[#4A6B56]" /> Escrow status
                </h2>
                <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
                  <EscrowBadge status={escrow.escrow_status} />
                  <div className="text-[#6B6965]">
                    {escrow.escrow_status === "held" && escrow.escrow_release_at && (
                      <>Releases <span className="text-[#1B3624] font-medium">{new Date(escrow.escrow_release_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</span></>
                    )}
                    {escrow.escrow_status === "released" && escrow.escrow_released_at && (
                      <>Released <span className="text-[#1B3624] font-medium">{new Date(escrow.escrow_released_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</span></>
                    )}
                    {escrow.escrow_status === "disputed" && <>Frozen pending admin review</>}
                    {escrow.escrow_status === "refunded" && <>Refunded to homeowner</>}
                  </div>
                  <div className="text-right text-[#1B3624] font-medium" style={{ fontFamily: "Outfit" }}>
                    £{Number(escrow.amount || 0).toFixed(2)}
                  </div>
                </div>
                {escrow.dispute_reason && (
                  <div className="mt-3 text-sm bg-red-50 text-red-800 rounded-xl p-3 border border-red-100">
                    <span className="font-medium">Dispute reason:</span> {escrow.dispute_reason}
                  </div>
                )}
                {canDispute && !alreadyDisputed && escrow.escrow_status !== "released" && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-xs text-[#6B6965] flex-1 min-w-[200px]">
                      Something went wrong? File a dispute before the escrow window closes — funds will freeze for admin review.
                    </p>
                    <Button
                      data-testid="dispute-job-btn"
                      onClick={fileDispute}
                      disabled={filingDispute}
                      variant="outline"
                      className="rounded-full border-red-200 text-red-700 hover:bg-red-50 min-h-[44px]"
                    >
                      <Gavel className="w-4 h-4 mr-1" /> {filingDispute ? "Filing…" : "File dispute"}
                    </Button>
                  </div>
                )}
              </Card>
            )}

            {/* In-app chat — accepted bid takes priority; falls back to the
                lowest / newest pending bidder so owners can DM before
                accepting, and every conversation exposes Report + Block. */}
            {(() => {
              const chatPartner = acceptedBid
                ? { id: acceptedBid.carrier_id, name: acceptedBid.carrier_name }
                : (isOwner && bids.length > 0)
                  ? { id: bids[0].carrier_id, name: bids[0].carrier_name }
                  : null;
              const canChat = !!chatPartner && (
                acceptedBid ? (isOwner || isAcceptedCarrier) : isOwner
              );
              if (!canChat) return null;
              return (
                <ChatPanel
                  currentUserId={user.id}
                  peerId={chatPartner.id}
                  peerName={chatPartner.name}
                  wastePostId={post.id}
                  viewerRole={user.role}
                  testIdPrefix="waste-chat"
                />
              );
            })()}

            {canReview && (
              <Card data-testid="review-form" className="rounded-2xl border-[#E2DFD8] p-6 bg-white">
                <h2 className="text-xl tracking-tight font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                  <Star className="w-5 h-5 text-[#D67B54]" /> Rate {acceptedBid.carrier_name}
                </h2>
                <p className="text-sm text-[#6B6965] mt-1">How did your collection go?</p>
                <form onSubmit={submitReview} className="mt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <StarRating value={reviewForm.rating} onChange={(r) => setReviewForm({ ...reviewForm, rating: r })} size={28} testId="review-stars" />
                    <span className="text-sm text-[#6B6965]">{reviewForm.rating}/5</span>
                  </div>
                  <Textarea
                    data-testid="review-comment"
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    placeholder="Optional comment about the service…"
                    className="rounded-xl"
                  />
                  <Button data-testid="review-submit" type="submit" disabled={submittingReview} className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white px-7 min-h-[44px]">
                    {submittingReview ? "Submitting…" : "Submit review"}
                  </Button>
                </form>
              </Card>
            )}

            {canReviewCustomer && (
              <Card data-testid="customer-review-form" className="rounded-2xl border-[#E2DFD8] p-6 bg-white">
                <h2 className="text-xl tracking-tight font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                  <Star className="w-5 h-5 text-[#D67B54]" /> Rate the customer
                </h2>
                <p className="text-sm text-[#6B6965] mt-1">How was {post.user_name} to work with? Was access easy, waste as described?</p>
                <form onSubmit={submitCustomerReview} className="mt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <StarRating value={customerReviewForm.rating} onChange={(r) => setCustomerReviewForm({ ...customerReviewForm, rating: r })} size={28} testId="customer-review-stars" />
                    <span className="text-sm text-[#6B6965]">{customerReviewForm.rating}/5</span>
                  </div>
                  <Textarea
                    data-testid="customer-review-comment"
                    value={customerReviewForm.comment}
                    onChange={(e) => setCustomerReviewForm({ ...customerReviewForm, comment: e.target.value })}
                    placeholder="Was the customer responsive, waste accurate, access easy?"
                    className="rounded-xl"
                  />
                  <Button data-testid="customer-review-submit" type="submit" disabled={submittingCustReview} className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white px-7 min-h-[44px]">
                    {submittingCustReview ? "Submitting…" : "Submit customer review"}
                  </Button>
                </form>
              </Card>
            )}

            {canBid && (
              <Card data-testid="bid-form" className="rounded-2xl border-[#E2DFD8] p-6 bg-white">
                <h2 className="text-xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>Submit a bid</h2>
                <form onSubmit={submitBid} className="mt-4 grid sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-1">
                    <Label className="text-sm">Amount (£)</Label>
                    <Input data-testid="bid-amount" required type="number" min="1" step="0.5" value={bid.amount} onChange={(e) => setBid({ ...bid, amount: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-sm">Proposed pickup time</Label>
                    <Input data-testid="bid-pickup-at" required type="datetime-local" value={bid.pickup_at} onChange={(e) => setBid({ ...bid, pickup_at: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                  <div className="sm:col-span-3">
                    <Label className="text-sm">Message</Label>
                    <Textarea data-testid="bid-message" required value={bid.message} onChange={(e) => setBid({ ...bid, message: e.target.value })} placeholder="Why choose you?" className="rounded-xl mt-1" />
                  </div>
                  <div className="sm:col-span-3">
                    <Button data-testid="bid-submit" type="submit" className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white px-7 py-5 min-h-[44px]">
                      Submit bid
                    </Button>
                  </div>
                </form>
              </Card>
            )}
          </div>

          <div className="space-y-4">
            {isAdminUser(user) && (
              <MapView
                markers={[{ id: post.id, lat: post.lat, lng: post.lng, title: post.title, subtitle: post.address }]}
                center={[post.lat, post.lng]}
                zoom={14}
                height="h-[280px]"
                testId="waste-detail-map"
              />
            )}
            <Card className="p-5 rounded-2xl border-[#E2DFD8] bg-[#1B3624] text-white">
              <ShieldCheck className="w-5 h-5 text-[#D67B54]" />
              <div className="mt-2 text-sm font-medium" style={{ fontFamily: "Outfit" }}>£500 Flytipping Guarantee</div>
              <div className="mt-1 text-xs text-white/70">
                Every carrier holds a verified Environment Agency waste-carrier licence. If your waste is illegally
                disposed of via a Binshare job, we'll cover your costs up to £500 — subject to our <a href="/policies" className="underline hover:text-white">terms</a>.
              </div>
            </Card>
            <Card className="p-5 rounded-2xl border-[#E2DFD8] bg-white">
              <ShieldCheck className="w-5 h-5 text-[#4A6B56]" />
              <div className="mt-2 text-sm font-medium" style={{ fontFamily: "Outfit" }}>Safe payments + escrow</div>
              <div className="mt-1 text-xs text-[#6B6965]">Funds clear to the carrier 24h after job completion — protecting both sides. Disputes pause the payout.</div>
            </Card>
            {/* Duty-of-care receipt — visible once a job is completed. */}
            {post.status !== "open" && post.status !== "accepted" && post.disposal_site && (
              <Card
                data-testid="duty-of-care-card"
                className="p-5 rounded-2xl border-[#E2DFD8] bg-[#F6F4F0]"
              >
                <div className="text-[10px] uppercase tracking-widest text-[#1B3624] font-semibold">Duty of care</div>
                <div className="mt-1 text-sm font-medium" style={{ fontFamily: "Outfit" }}>Disposal site / method</div>
                <div
                  data-testid="disposal-site-value"
                  className="mt-1 text-sm text-[#1A1A1A] whitespace-pre-wrap"
                >{post.disposal_site}</div>
                <div className="mt-2 text-[11px] text-[#6B6965]">
                  Recorded by {acceptedBid?.carrier_name || "your carrier"} · Ref {post.id.slice(0, 8).toUpperCase()}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent data-testid="complete-dialog" className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mark this job complete</DialogTitle>
            <DialogDescription>
              Duty of care — please record where you disposed of this waste and how much stayed out of landfill.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-sm">Disposal site / method</Label>
            <Input
              data-testid="disposal-site-input"
              value={disposalSite}
              onChange={(e) => setDisposalSite(e.target.value)}
              placeholder="e.g. Biffa MRF Manchester · Donated to BHF charity shop"
              maxLength={280}
              className="rounded-xl mt-1"
            />
            <p className="mt-1 text-[11px] text-[#6B6965]">
              Required to release escrow. Visible to the customer.
            </p>
          </div>
          {/* Phase 5.1 — reuse breakdown. Three integer % fields that must
              sum to exactly 100. Rendered only for the carrier; when the
              homeowner falls back to completing, we don't ask them. */}
          {isAcceptedCarrier && (() => {
            const r = Number.parseInt(reusedPct || "0", 10);
            const c = Number.parseInt(recycledPct || "0", 10);
            const l = Number.parseInt(landfilledPct || "0", 10);
            const sum = r + c + l;
            const filled = reusedPct !== "" || recycledPct !== "" || landfilledPct !== "";
            const remaining = 100 - sum;
            return (
              <div data-testid="reuse-breakdown-section" className="mt-4 pt-4 border-t border-[#E2DFD8]">
                <Label className="text-sm">How was this waste handled?</Label>
                <p className="text-[11px] text-[#6B6965] mt-1">
                  Three numbers that add up to 100. Customers see this on the completed-jobs feed.
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <div>
                    <Label htmlFor="reuse-pct-reused" className="text-[11px] text-[#4A6B56] uppercase tracking-wide">Reused</Label>
                    <Input
                      id="reuse-pct-reused"
                      data-testid="reuse-pct-reused"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={100}
                      value={reusedPct}
                      onChange={(e) => setReusedPct(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
                      placeholder="0"
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reuse-pct-recycled" className="text-[11px] text-[#1B3624] uppercase tracking-wide">Recycled</Label>
                    <Input
                      id="reuse-pct-recycled"
                      data-testid="reuse-pct-recycled"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={100}
                      value={recycledPct}
                      onChange={(e) => setRecycledPct(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
                      placeholder="0"
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reuse-pct-landfill" className="text-[11px] text-[#6B6965] uppercase tracking-wide">Landfill</Label>
                    <Input
                      id="reuse-pct-landfill"
                      data-testid="reuse-pct-landfill"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={100}
                      value={landfilledPct}
                      onChange={(e) => setLandfilledPct(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
                      placeholder="0"
                      className="rounded-xl mt-1"
                    />
                  </div>
                </div>
                <div
                  data-testid="reuse-pct-sum"
                  className={`mt-2 text-[11px] font-medium ${filled && sum === 100 ? "text-[#4A6B56]" : filled ? "text-[#B84E2A]" : "text-[#6B6965]"}`}
                >
                  {!filled && "Enter each percentage — needs to total 100."}
                  {filled && sum === 100 && "✓ Adds up to 100%"}
                  {filled && sum !== 100 && `Currently ${sum}% — ${remaining > 0 ? `${remaining}% left to allocate` : `over by ${Math.abs(remaining)}%`}`}
                </div>
                <div className="mt-3">
                  <Label htmlFor="disposal-notes" className="text-[11px] text-[#6B6965] uppercase tracking-wide">Notes (optional)</Label>
                  <Input
                    id="disposal-notes"
                    data-testid="disposal-notes-input"
                    value={disposalNotes}
                    onChange={(e) => setDisposalNotes(e.target.value)}
                    placeholder="e.g. Sofa donated locally, timber to workshop"
                    maxLength={500}
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button
              data-testid="complete-cancel"
              variant="ghost"
              onClick={() => setCompleteOpen(false)}
              className="rounded-full text-[#6B6965]"
            >
              Cancel
            </Button>
            <Button
              data-testid="complete-confirm"
              onClick={markComplete}
              disabled={
                completing
                || disposalSite.trim().length < 3
                || (
                  isAcceptedCarrier && (
                    reusedPct === "" || recycledPct === "" || landfilledPct === ""
                    || (Number.parseInt(reusedPct||"0",10) + Number.parseInt(recycledPct||"0",10) + Number.parseInt(landfilledPct||"0",10)) !== 100
                  )
                )
              }
              className="rounded-full bg-[#4A6B56] hover:bg-[#3d5947] text-white"
            >
              {completing ? "Marking…" : "Mark complete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Task 1 — Price Variation dialog (carrier requests on-site revision) */}
      <Dialog open={variationOpen} onOpenChange={setVariationOpen}>
        <DialogContent data-testid="variation-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Price Variation</DialogTitle>
            <DialogDescription>
              Current quote: <strong>£{acceptedBid ? Number(acceptedBid.amount).toFixed(2) : "0.00"}</strong>. Propose a new price and document the reason — the customer will accept or decline.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Proposed revised price (£)</Label>
              <Input
                data-testid="variation-new-amount"
                type="number"
                inputMode="decimal"
                min="1"
                step="0.01"
                value={variationForm.new_amount}
                onChange={(e) => setVariationForm((f) => ({ ...f, new_amount: e.target.value }))}
                placeholder="e.g. 85.00"
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Reason</Label>
              <Textarea
                data-testid="variation-reason"
                value={variationForm.reason}
                onChange={(e) => setVariationForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Extra sofa found on-site not in the original description"
                maxLength={500}
                rows={3}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Photo (optional)</Label>
              <Input
                data-testid="variation-photo-input"
                type="file"
                accept="image/*"
                onChange={(e) => uploadVariationPhoto(e.target.files?.[0])}
                disabled={variationUploading}
                className="rounded-xl mt-1"
              />
              {variationUploading && <p className="mt-1 text-[11px] text-[#6B6965]">Uploading…</p>}
              {variationForm.photo_url && (
                <img src={variationForm.photo_url} alt="Attached evidence" className="mt-2 max-w-[180px] rounded-lg border border-[#E2DFD8]" />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="variation-cancel"
              variant="ghost"
              onClick={() => setVariationOpen(false)}
              className="rounded-full text-[#6B6965]"
            >
              Cancel
            </Button>
            <Button
              data-testid="variation-submit"
              onClick={submitVariation}
              disabled={amending || variationUploading}
              className="rounded-full bg-[#D67B54] hover:bg-[#c66c44] text-white"
            >
              {amending ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {post && (
        <ReportDialog
          open={reportOpen}
          onOpenChange={setReportOpen}          userId={user?.id}
          targetKind="waste_post"
          targetId={post.id}
          targetLabel={post.title}
        />
      )}
      {reportCarrier && (
        <ReportDialog
          open={!!reportCarrier}
          onOpenChange={(v) => { if (!v) setReportCarrier(null); }}
          userId={user?.id}
          targetKind="user"
          targetId={reportCarrier.id}
          targetLabel={reportCarrier.name}
        />
      )}
    </Layout>
  );
}

// Phase 2 — sub-category labels + field pretty-printers for the details block.
const SUBTYPE_LABELS = {
  house: "House clearance",
  garden: "Garden clearance",
  end_of_tenancy: "End of tenancy",
  moving_house: "Moving house",
  diy_renovation: "DIY & renovation",
  commercial: "Commercial",
};

const DETAIL_FIELDS = {
  house: [
    { key: "rooms", label: "Rooms" },
    { key: "floors", label: "Floors" },
    { key: "hazards", label: "Hazardous items" },
  ],
  garden: [
    { key: "size", label: "Approx size" },
    { key: "contains_soil", label: "Contains soil", bool: true },
    { key: "treated_timber", label: "Treated timber", bool: true },
  ],
  end_of_tenancy: [
    { key: "tenancy_end", label: "Tenancy end" },
    { key: "key_handover", label: "Key handover" },
    { key: "landlord_contact", label: "Landlord / agent" },
  ],
  moving_house: [
    { key: "move_date", label: "Move date" },
    { key: "destination_postcode", label: "Destination" },
    { key: "rooms", label: "Rooms to clear" },
  ],
  diy_renovation: [
    { key: "materials", label: "Materials" },
    { key: "asbestos", label: "Asbestos suspected", bool: true },
  ],
  commercial: [
    { key: "business_type", label: "Business" },
    { key: "weekly_volume", label: "Weekly volume" },
    { key: "access_hours", label: "Access hours" },
    { key: "weee", label: "Contains WEEE", bool: true },
  ],
};

function ClearanceDetails({ subtype, details }) {
  const fields = DETAIL_FIELDS[subtype] || [];
  const rows = fields
    .map((f) => {
      const raw = details[f.key];
      if (f.bool) {
        return raw ? { label: f.label, value: "Yes" } : null;
      }
      if (raw === undefined || raw === null || raw === "") return null;
      if (Array.isArray(raw)) {
        if (raw.length === 0) return null;
        return { label: f.label, value: raw.join(", ") };
      }
      return { label: f.label, value: String(raw) };
    })
    .filter(Boolean);
  if (rows.length === 0) return null;
  return (
    <div data-testid="waste-clearance-details" className="mt-5 rounded-2xl border border-[#1B3624]/15 bg-[#F6F4F0] p-4">
      <div className="text-[11px] uppercase tracking-widest text-[#1B3624] font-medium">
        {SUBTYPE_LABELS[subtype] || "Clearance"} details
      </div>
      <dl className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        {rows.map((r) => (
          <div key={r.label} data-testid={`clearance-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-baseline gap-2">
            <dt className="text-[#6B6965] shrink-0">{r.label}:</dt>
            <dd className="text-[#1A1A1A] font-medium min-w-0 break-words">{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}


function EscrowBadge({ status }) {
  const map = {
    held: { cls: "bg-[#D67B54] text-white", label: "Held in escrow" },    released: { cls: "bg-[#4A6B56] text-white", label: "Released" },
    disputed: { cls: "bg-red-600 text-white", label: "Disputed" },
    refunded: { cls: "bg-[#1B3624] text-white", label: "Refunded" },
  };
  const m = map[status] || { cls: "bg-[#EBE8E0] text-[#1B3624]", label: status || "—" };
  return <Badge data-testid={`escrow-badge-${status || "none"}`} className={`${m.cls} hover:${m.cls} uppercase text-[10px] self-start`}>{m.label}</Badge>;
}
